import { t } from '@lingui/core/macro';
import type { UserRecord } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { ErrorMessage, NoddiDialog, useAppForm } from 'noddi-ui-web';
import { useState } from 'react';
import { useContactVerificationRequired } from '../../hooks/useContactVerification';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { ApiErrorMessageWithTrans } from '../Error/ApiErrorMessageWithTrans';
import { ConfirmPromptFooter } from './ConfirmPromptFooter';
import { ContactVerificationCode, VERIFICATION_CODE_LENGTH } from './ContactVerificationCode';
import type { CodeSentTo, ContactDraft } from './types';
import { PERSONAL_INFO_REVIEW_MUTATION_KEY } from './types';
import { useReviewMutationsPending } from './useReviewMutationsPending';
import { useUpdateSessionUser } from './useUpdateSessionUser';
import { getContactValidationSchema } from './validators';

const CONTACT_FORM_ID = 'confirm-personal-info-contact';

type Props = {
  user: UserRecord;
  onSaved: () => void;
  onSkip: () => void;
};

export const ConfirmContactDialog = ({ user, onSaved, onSkip }: Props) => {
  const { currentBrand } = useCurrentBrand();

  const registeredContact: ContactDraft = {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email ?? '',
    phoneNumber: user.phoneNumber ?? ''
  };

  const [codeSentTo, setCodeSentTo] = useState<CodeSentTo>({});
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [phoneNumberVerificationCode, setPhoneNumberVerificationCode] = useState('');
  const [missingCodeMessage, setMissingCodeMessage] = useState<string | null>(null);

  const isMutating = useReviewMutationsPending();
  const requiresVerification = useContactVerificationRequired();

  const updateSessionUser = useUpdateSessionUser();

  const {
    mutate: sendEmailVerification,
    isPending: isSendingEmailCode,
    error: sendEmailError
  } = noddiAsync.usePost({
    type: URLKeys.postSendEmailVerification,
    queryConfig: { mutationKey: PERSONAL_INFO_REVIEW_MUTATION_KEY }
  });

  const {
    mutate: sendPhoneNumberVerification,
    isPending: isSendingPhoneNumberCode,
    error: sendPhoneNumberError
  } = noddiAsync.usePost({
    type: URLKeys.postSendPhoneNumberVerification,
    queryConfig: { mutationKey: PERSONAL_INFO_REVIEW_MUTATION_KEY }
  });

  // update-contact does not stamp personal_info_confirmed_at — only the final confirm does — so
  // abandoning the review half way keeps what was typed without recording the review as finished.
  const { mutateAsync: updateContact, error: updateContactError } = noddiAsync.usePost({
    type: URLKeys.postUpdateContact,
    queryConfig: {
      mutationKey: PERSONAL_INFO_REVIEW_MUTATION_KEY,
      // The session copy of the user is refreshed here, not only the query
      // cache: a customer who changes their name and then chooses Remind me
      // later never reaches a final confirm, and would otherwise keep being
      // greeted by the old name until a reload.
      onSuccess: (response) => {
        updateSessionUser(response.data);
        invalidateQueryExactMatch({ urlKey: URLKeys.getAccount, input: { userId: user.id } });
      }
    }
  });

  // The code box is revealed from onSuccess, never optimistically: a failed send would otherwise
  // leave the user staring at six empty slots waiting for a code that was never dispatched. Any
  // code already typed is dropped, so an old one cannot be submitted against a fresh challenge.
  const requestEmailCode = (email: string) =>
    sendEmailVerification(
      { email },
      {
        onSuccess: () => {
          setEmailVerificationCode('');
          setCodeSentTo((current) => ({ ...current, email }));
        }
      }
    );

  const requestPhoneNumberCode = (phoneNumber: string, forceSend = false) =>
    sendPhoneNumberVerification(
      { phoneNumber, domain: currentBrand, forceSend },
      {
        onSuccess: () => {
          setPhoneNumberVerificationCode('');
          setCodeSentTo((current) => ({ ...current, phoneNumber }));
        }
      }
    );

  const form = useAppForm({
    defaultValues: registeredContact,
    validators: {
      onSubmit: getContactValidationSchema()
    },
    onSubmit: async ({ value }) => {
      const isEmailChanged = value.email !== '' && value.email !== registeredContact.email;
      const isPhoneNumberChanged = value.phoneNumber !== '' && value.phoneNumber !== registeredContact.phoneNumber;

      // First Confirm after changing a contact field asks for the code instead of saving. Leave the
      // details alone and this branch never runs, so confirming stays a single click.
      const needsEmailCode = requiresVerification && isEmailChanged && codeSentTo.email !== value.email;
      const needsPhoneNumberCode =
        requiresVerification && isPhoneNumberChanged && codeSentTo.phoneNumber !== value.phoneNumber;

      if (needsEmailCode || needsPhoneNumberCode) {
        if (needsEmailCode) {
          requestEmailCode(value.email);
        }
        if (needsPhoneNumberCode) {
          requestPhoneNumberCode(value.phoneNumber);
        }
        setMissingCodeMessage(null);
        return;
      }

      const isEmailCodeMissing =
        requiresVerification && isEmailChanged && emailVerificationCode.length !== VERIFICATION_CODE_LENGTH;
      const isPhoneCodeMissing =
        requiresVerification && isPhoneNumberChanged && phoneNumberVerificationCode.length !== VERIFICATION_CODE_LENGTH;

      if (isEmailCodeMissing || isPhoneCodeMissing) {
        setMissingCodeMessage(t`Enter the code we sent you.`);
        return;
      }

      setMissingCodeMessage(null);

      // Name can stay blank. Email and phone cannot: the schema rejects an empty one,
      // and a value that differs from what we had on file is what gets saved.
      const isFirstNameChanged = value.firstName !== '' && value.firstName !== registeredContact.firstName;
      const isLastNameChanged = value.lastName !== '' && value.lastName !== registeredContact.lastName;

      const hasChanges = isFirstNameChanged || isLastNameChanged || isEmailChanged || isPhoneNumberChanged;

      if (hasChanges) {
        try {
          await updateContact({
            ...(isFirstNameChanged ? { firstName: value.firstName } : {}),
            ...(isLastNameChanged ? { lastName: value.lastName } : {}),
            ...(isEmailChanged ? { email: value.email, emailVerificationCode } : {}),
            ...(isPhoneNumberChanged ? { phoneNumber: value.phoneNumber, phoneNumberVerificationCode } : {})
          });
        } catch {
          // The refusal is rendered inline from the mutation state; swallowed to avoid an unhandled rejection.
          return;
        }
      }

      onSaved();
    }
  });

  // Verification failures now arrive with their own error codes, so the shared
  // translated map handles them like any other backend error -- no inspecting
  // the error's attr to work out what went wrong.
  const contactError = updateContactError;
  const sendCodeError = sendEmailError ?? sendPhoneNumberError;

  const errorContent = missingCodeMessage ? (
    <ErrorMessage message={missingCodeMessage} />
  ) : contactError ? (
    <ApiErrorMessageWithTrans error={contactError} />
  ) : sendCodeError ? (
    <ErrorMessage message={t`We could not send the code. Please try again.`} />
  ) : null;

  return (
    <form.AppForm>
      <NoddiDialog
        open
        // Answered with Confirm or Skip — never by clicking outside the prompt.
        onClose={() => {}}
        showCloseButton={false}
        className='max-h-[85vh]'
        title={t`Are your details still correct?`}
        description={t`This is what we have on file — change anything that is wrong.`}
        footer={
          <ConfirmPromptFooter
            errorContent={errorContent}
            isBusy={isMutating}
            onSkip={onSkip}
            primaryAction={
              <form.SubscribeButton
                formId={CONTACT_FORM_ID}
                label={t`Confirm`}
                disabled={isMutating || isSendingEmailCode || isSendingPhoneNumberCode}
              />
            }
          />
        }
      >
        <form
          id={CONTACT_FORM_ID}
          className='flex flex-col gap-5'
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <form.AppField name='firstName'>{(field) => <field.Input label={t`First name`} />}</form.AppField>
            <form.AppField name='lastName'>{(field) => <field.Input label={t`Last name`} />}</form.AppField>
          </div>

          <div className='flex flex-col gap-2'>
            <form.AppField name='phoneNumber'>{(field) => <field.PhoneInput label={t`Phone number`} />}</form.AppField>
            {requiresVerification && codeSentTo.phoneNumber && (
              <ContactVerificationCode
                kind='phoneNumber'
                destination={codeSentTo.phoneNumber}
                code={phoneNumberVerificationCode}
                isResending={isSendingPhoneNumberCode}
                onCodeChange={setPhoneNumberVerificationCode}
                onResend={() => requestPhoneNumberCode(codeSentTo.phoneNumber as string, true)}
              />
            )}
          </div>

          <div className='flex flex-col gap-2'>
            <form.AppField name='email'>{(field) => <field.Input label={t`Email`} type='email' />}</form.AppField>
            {requiresVerification && codeSentTo.email && (
              <ContactVerificationCode
                kind='email'
                destination={codeSentTo.email}
                code={emailVerificationCode}
                isResending={isSendingEmailCode}
                onCodeChange={setEmailVerificationCode}
                onResend={() => requestEmailCode(codeSentTo.email as string)}
              />
            )}
          </div>
        </form>
      </NoddiDialog>
    </form.AppForm>
  );
};
